import { useMemo } from "react";
import { useQuery } from "react-query";
import { normalizeAirportCode } from "../utils/airportCodeUtils";
import { referenceDataQueryKeys } from "../features/flightSchedule/ScheduleDraft/hooks/queryKeys";
import { scheduleDraftReferenceDataService } from "../features/flightSchedule/ScheduleDraft/services/scheduleDraftReferenceDataService";
import useAirports from "./useAirports";
import useAircrafts from "./useAircrafts";
import useFlightLegs from "./useFlightLegs";

const REFERENCE_ROUTE_STALE_TIME = 5 * 60 * 1000;
const REFERENCE_CACHE_TIME = 30 * 60 * 1000;
// 机场最小过站会被运控临时修改，且自动填时刻的取值依赖它：短缓存 + 回到页面时
// 重新拉取，保证“页开着改完主数据，切回来就用新值”，不必先点刷新。
const AIRPORT_CONSTRAINTS_STALE_TIME = 30 * 1000;

export { useAirports, useAircrafts, useFlightLegs };

export const useFlightRoutesByLeg = (departureAirport, arrivalAirport, options = {}) => {
    const query = useQuery(
        referenceDataQueryKeys.flightRoutes(departureAirport, arrivalAirport),
        () => scheduleDraftReferenceDataService.getRoutesByLeg(departureAirport, arrivalAirport),
        {
            enabled: Boolean(departureAirport && arrivalAirport) && (options.enabled ?? true),
            staleTime: REFERENCE_ROUTE_STALE_TIME,
            cacheTime: REFERENCE_CACHE_TIME,
            refetchOnWindowFocus: false,
            ...options,
        },
    );

    return {
        routes: query.data || [],
        isLoading: query.isLoading,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error,
        refetch: query.refetch,
    };
};

export const useAirportConstraints = (options = {}) => {
    const query = useQuery(referenceDataQueryKeys.airportConstraints(), () => scheduleDraftReferenceDataService.getAirportConstraints(), {
        staleTime: AIRPORT_CONSTRAINTS_STALE_TIME,
        cacheTime: REFERENCE_CACHE_TIME,
        // “always”：staleTime 窗口内切回页面也强制重拉，避免运控刚改完主数据
        // 切回来时表格仍展示旧过站
        refetchOnWindowFocus: "always",
        ...options,
    });

    const constraints = query.data || [];
    const constraintsMap = useMemo(
        () =>
            constraints.reduce((acc, constraint) => {
                // 键统一归一化成三字码：写入端若带小写/空格，未归一化的键会让查配置静默落空，
                // 取值方再回落到航班上的旧数字，假 1 小时就从这条路回来
                const code = normalizeAirportCode(constraint.iata_code || constraint.code || constraint.airport_code);
                if (code) {
                    acc[code] = constraint;
                }
                return acc;
            }, {}),
        [constraints],
    );

    return {
        constraints,
        constraintsMap,
        isLoading: query.isLoading,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error,
        refetch: query.refetch,
    };
};
